import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { of } from 'rxjs';

import StepIndicator from '../../../components/common/step-indicator';
import { SubmitType } from '../../../components/form';
import { LIVESTOCK_FODDER_NEED_INDEX } from '../../../router/routes';
import { useService } from '../../../services/services';
import { deepCopy, route, periodsDiffInDays } from '../../../services/utils';
import StepPeriods from './step-periods';
import StepFeedingstuffs, {
  FeedingstuffsSubmitData,
} from './step-feedingstuffs';
import StepAnimalGroups, {
  AnimalsGroupsSubmitData,
} from './step-animal-groups';
import StepSummary, {
  SummaryFeedingstuffRow,
  SummarySubmitData,
} from './step-summary';
import { FodderNeedStepKey } from '../../../enums/fodder-need-step-keys';
import Feedingstuff from '../../../interfaces/feedingstuff';
import { PeriodsSubmitData } from './step-periods';
import FodderNeedPeriodType from '../../../enums/fodder-need-period-type';
import FodderNeed from '../../../interfaces/fodder-need';
import AnimalGroup from '../../../interfaces/animal-group';
import { Option } from '../../../components/form/elements/select';

export interface FormData
  extends PeriodsSubmitData,
    FeedingstuffsSubmitData,
    AnimalsGroupsSubmitData,
    SummarySubmitData {
  id?: string;
  farm_id: string;
  selectedPeriod?: string | null;
  feedingstuffsClassifiers: Option[];
}

export function formDataFromReq(
  fodderNeed: FodderNeed,
  farmId: string,
  periods: any
): FormData {
  let fData = {
    id: fodderNeed.id,
    periods: {
      [FodderNeedPeriodType.SUMMER]: { isSummerPeriod: true },
      [FodderNeedPeriodType.WINTER_SPRING]: { isSummerPeriod: false },
      [FodderNeedPeriodType.WINTER]: { isSummerPeriod: false },
    },
    feedingstuffs: {},
    animalGroups: {},
    summary: {},
    periodsDaysCount: {
      [FodderNeedPeriodType.WINTER]: 0,
      [FodderNeedPeriodType.SUMMER]: 0,
    },
    usedPeriods: [],
    farm_id: farmId,
    selectedPeriod: null,
    feedingstuffsClassifiers: [],
  } as FormData;

  fodderNeed.periods.forEach((period) => {
    fData.periods[period.period_type] = {
      ...fData.periods[period.period_type],
      ...period,
    };
    fData.periodsDaysCount[
      fData.periods[period.period_type].isSummerPeriod
        ? FodderNeedPeriodType.SUMMER
        : FodderNeedPeriodType.WINTER
    ] = periodsDiffInDays(new Date(period.until), new Date(period.from));
  });
  fodderNeed.feedingstuffs.forEach((feedingstuff, index) => {
    feedingstuff.planned_to_produce_t =
      feedingstuff.yield_avg * feedingstuff.planned_land_ha;
    fData.feedingstuffs[index] = feedingstuff;
  });

  periods.forEach((period: string) => {
    fData.animalGroups[period] = {};

    //if one of periods do not exists then replace it with existing period data
    let newPeriod = null as string | null;
    if (!(period in fodderNeed.animalGroups)) {
      newPeriod = periods.find((per: any) => per !== period);
    }

    fodderNeed.animalGroups[newPeriod ?? period].forEach(
      (aGroup: AnimalGroup, index: number) => {
        const aGroupDuplicated = { ...aGroup };
        if (newPeriod) {
          aGroupDuplicated.animal_count = null;
          aGroupDuplicated.feedingstuffs = [];
          delete aGroupDuplicated.id;
        }
        aGroupDuplicated.feedingstuffs.forEach(
          (feedingstuf: any, index: number) => {
            feedingstuf.amount_in_period_for_animal_group =
              aGroupDuplicated.animal_count *
              feedingstuf.ammount_in_day_on_animal *
              fData.periodsDaysCount[period];
            aGroupDuplicated.feedingstuffs[index] = feedingstuf;
          }
        );
        fData.animalGroups[period][index] = aGroupDuplicated;
      }
    );

    //set summary data
    fData.summary[period] = {};
    if (period in fodderNeed.summary) {
      fodderNeed.summary[period].forEach(
        (summaryRow: SummaryFeedingstuffRow, index: number) => {
          fData.summary[period][summaryRow.feedingstuff_id] = summaryRow;
        }
      );
    }
  });

  return fData;
}

export default function CreateFodderNeed() {
  const history = useHistory();
  const { farmId, id } = useParams<{
    farmId: string;
    id: string;
  }>();
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const { fodderNeedService } = useService();

  const [formData, setFormData] = useState<FormData>(null);
  const [step, setStep] = useState(0);
  const [stepKey, setStepKey] = useState(FodderNeedStepKey.PERIODS);

  const periods = useMemo(
    () => [FodderNeedPeriodType.WINTER, FodderNeedPeriodType.SUMMER],
    []
  );
  useEffect(() => {
    // edit page
    if (id) {
      fodderNeedService
        .show(farmId, id)
        .subscribe(async (fodderNeed: FodderNeed) => {
          setFormData(formDataFromReq(fodderNeed, farmId, periods));
        });
    } else {
      let fData = {
        id: id ?? null,
        periods: {
          [FodderNeedPeriodType.SUMMER]: { isSummerPeriod: true },
          [FodderNeedPeriodType.WINTER_SPRING]: { isSummerPeriod: false },
          [FodderNeedPeriodType.WINTER]: { isSummerPeriod: false },
        },
        feedingstuffs: {},
        animalGroups: {},
        summary: {},
        periodsDaysCount: {
          [FodderNeedPeriodType.WINTER]: 0,
          [FodderNeedPeriodType.SUMMER]: 0,
        },
        usedPeriods: [],
        farm_id: farmId,
        selectedPeriod: null,
        feedingstuffsClassifiers: [],
      } as FormData;

      periods.forEach((period: string) => {
        fData.animalGroups[period] = {};
        fData.summary[period] = {};
      });
      setFormData(fData);
    }
  }, [id, fodderNeedService, periods, farmId]);

  const returnStep = () => {
    switch (stepKey) {
      case FodderNeedStepKey.FEEDINGSTUFFS:
        setStepKey(FodderNeedStepKey.PERIODS);
        break;
      case FodderNeedStepKey.ANIMAL_GROUPS:
        setStepKey(FodderNeedStepKey.FEEDINGSTUFFS);
        break;
      case FodderNeedStepKey.SUMMARY:
        setStepKey(FodderNeedStepKey.ANIMAL_GROUPS);
        break;
    }
    setStep((step) => step - 1);
  };

  const submitPeriods = useCallback<SubmitType<any>>(
    (stepData) => {
      stepData.periodsDaysCount[FodderNeedPeriodType.SUMMER] = 0;
      stepData.periodsDaysCount[FodderNeedPeriodType.WINTER] = 0;

      stepData.usedPeriods = Array.from(
        new Set(
          Object.entries(stepData.periods)
            .filter(([key, value]: [string, any]) => {
              if (value.from && value.until) {
                stepData.periodsDaysCount[
                  value.isSummerPeriod
                    ? FodderNeedPeriodType.SUMMER
                    : FodderNeedPeriodType.WINTER
                ] += periodsDiffInDays(
                  new Date(value.until),
                  new Date(value.from)
                );
                return true;
              }
              return false;
            })
            .map(([key, value]: [string, any]) =>
              !value.isSummerPeriod
                ? FodderNeedPeriodType.WINTER
                : FodderNeedPeriodType.SUMMER
            )
            .sort((a, b) => (a > b ? -1 : 1))
        )
      );

      periods.forEach((period: string) => {
        Object.entries(stepData.animalGroups[period]).forEach(
          ([key, group]) => {
            stepData.animalGroups[period][key].feedingstuffs.forEach(
              (feedingstuf: any, index: number) => {
                stepData.animalGroups[period][key].feedingstuffs[
                  index
                ].amount_in_period_for_animal_group =
                  stepData.animalGroups[period][key].animal_count *
                  feedingstuf.ammount_in_day_on_animal *
                  stepData.periodsDaysCount[period];
              }
            );
          }
        );
      });

      setFormData({ ...formData, ...deepCopy(stepData) });
      setStepKey(FodderNeedStepKey.FEEDINGSTUFFS);
      setStep((step) => step + 1);
      return [of(null)];
    },
    [formData, periods]
  );

  const submitFeedingstuff = useCallback<SubmitType<FeedingstuffsSubmitData>>(
    (stepData) => {
      const obj = Object.assign(
        {} as { [key: string]: Feedingstuff },
        Object.values(stepData.feedingstuffs).filter(
          (feedingstuff: Feedingstuff) => feedingstuff.feedingstuff_id
        )
      );
      stepData.feedingstuffs = obj;

      if (formData.id) {
        periods.forEach((period: string) => {
          //remove animal groups feedingstuffs if not present in setp - feedingstuffs
          Object.keys(formData['animalGroups'][period]).forEach(
            (animalGroupKey: string) => {
              Object.entries(
                formData['animalGroups'][period][animalGroupKey].feedingstuffs
              ).forEach(([feedingstuffKey, feedingstuff]: [any, any]) => {
                if (
                  !Object.values(stepData.feedingstuffs).find(
                    (fStuff: any) =>
                      fStuff.feedingstuff_id === feedingstuff.feedingstuff_id
                  )
                ) {
                  delete formData['animalGroups'][period][animalGroupKey]
                    .feedingstuffs[feedingstuffKey];
                }
              });

              //recreate indexes without gaps
              formData['animalGroups'][period][animalGroupKey].feedingstuffs =
                Array.from(
                  formData['animalGroups'][period][
                    animalGroupKey
                  ].feedingstuffs.filter(Boolean)
                );
            }
          );
        });
      }

      setFormData({
        ...formData,
        feedingstuffs: { ...deepCopy(stepData.feedingstuffs) },
      });
      setStepKey(FodderNeedStepKey.ANIMAL_GROUPS);
      setStep((step) => step + 1);
      return [of(null)];
    },
    [formData, periods]
  );

  const saveFormData = useCallback<SubmitType<any>>(
    (stepData) => {
      setFormData({ ...formData, ...deepCopy(stepData) });
      return [of(null)];
    },
    [formData]
  );

  const saveAnimalFormData = useCallback<SubmitType<any>>(
    (stepData) => {
      periods.forEach((period) => {
        stepData.animalGroups[period] = Object.assign(
          {} as { [key: string]: AnimalGroup },
          Object.values(stepData.animalGroups[period])
        );
        Object.keys(stepData.animalGroups[period]).forEach((animalGroupKey) => {
          if (stepData.animalGroups[period][animalGroupKey].feedingstuffs) {
            stepData.animalGroups[period][animalGroupKey].feedingstuffs =
              stepData.animalGroups[period][
                animalGroupKey
              ].feedingstuffs.filter(Boolean);
          }
        });
      });

      setFormData({ ...formData, ...deepCopy(stepData) });
      return [of(null)];
    },
    [formData, periods]
  );

  const submitAnimalGroups = useCallback<SubmitType<AnimalsGroupsSubmitData>>(
    (stepData) => {
      periods.forEach((period) => {
        stepData.animalGroups[period] = Object.assign(
          {} as { [key: string]: AnimalGroup },
          Object.values(stepData.animalGroups[period])
        );
        Object.keys(stepData.animalGroups[period]).forEach((animalGroupKey) => {
          if (stepData.animalGroups[period][animalGroupKey].feedingstuffs) {
            stepData.animalGroups[period][animalGroupKey].feedingstuffs =
              stepData.animalGroups[period][
                animalGroupKey
              ].feedingstuffs.filter(Boolean);
          }
        });
      });

      setFormData({ ...formData, ...deepCopy(stepData) });
      setStepKey(FodderNeedStepKey.SUMMARY);
      setStep((step) => step + 1);
      return [of(null)];
    },
    [formData, periods]
  );

  const formDataToFodderNeed = useCallback((formData: FormData) => {
    let fodderNeed = {
      id: formData.id,
      periods: Object.entries(formData.periods)
        .map(([type, item]: [string, any]) => {
          if (item.from && item.until) {
            return {
              period_type: type,
              from: item.from,
              until: item.until,
              id: item.id,
            };
          }
          return null;
        })
        .filter(Boolean),
      feedingstuffs: Object.values(formData.feedingstuffs).map(
        (feedingstuff: any) => {
          const obj = {
            ...feedingstuff,
            title: feedingstuff.title?.value ?? feedingstuff.title,
          };
          const { planned_to_produce_t, ...rest } = obj;
          return rest;
        }
      ),
      animalGroups: {},
      summary: {},
    } as FodderNeed;

    Object.values(formData.usedPeriods).forEach((period: string) => {
      fodderNeed.animalGroups[period] = [];
      Object.values(formData.animalGroups[period]).forEach(
        (animalGroup: AnimalGroup) => {
          fodderNeed.animalGroups[period].push(animalGroup);
        }
      );
    });

    Object.values(formData.usedPeriods).forEach((period: string) => {
      fodderNeed.summary[period] = [];
      Object.entries(formData.summary[period]).forEach(
        ([feedingstuffId, summaryRow]: [string, SummaryFeedingstuffRow]) => {
          fodderNeed.summary[period].push({
            ...summaryRow,
            feedingstuff_id: feedingstuffId,
          });
        }
      );
    });

    return fodderNeed;
  }, []);

  const submitSummary = useCallback<SubmitType<null, FodderNeed>>(
    (stepData) => {
      const fodderNeed = formDataToFodderNeed(stepData);
      if (formData.id) {
        return [
          fodderNeedService.update(farmId, formData.id, fodderNeed),
          (response) =>
            history.push(
              route(LIVESTOCK_FODDER_NEED_INDEX, {
                farmId: farmId,
              })
            ),
        ];
      } else {
        return [
          fodderNeedService.store(farmId, fodderNeed),
          (response) =>
            history.push(
              route(LIVESTOCK_FODDER_NEED_INDEX, {
                farmId: farmId,
              })
            ),
        ];
      }
    },
    [formData, history, farmId, fodderNeedService, formDataToFodderNeed]
  );

  const steps = [
    { name: tp('periods') },
    { name: tp('feedingstuffs') },
    { name: tp('animal_groups') },
    { name: tp('summary') },
  ];

  if (formData === null) {
    return null;
  }

  return (
    <div>
      <StepIndicator steps={steps} currentStep={step} />
      {stepKey === FodderNeedStepKey.PERIODS && (
        <StepPeriods formData={formData} onSubmit={submitPeriods} />
      )}
      {stepKey === FodderNeedStepKey.FEEDINGSTUFFS && (
        <StepFeedingstuffs
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitFeedingstuff}
          saveFormData={saveFormData}
        />
      )}
      {stepKey === FodderNeedStepKey.ANIMAL_GROUPS && (
        <StepAnimalGroups
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitAnimalGroups}
          saveFormData={saveAnimalFormData}
          periods={periods}
        />
      )}
      {stepKey === FodderNeedStepKey.SUMMARY && (
        <StepSummary
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitSummary}
          periods={periods}
          saveFormData={saveFormData}
        />
      )}
    </div>
  );
}
